import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import {
    Drawer,
    Toolbar,
    List,
    Typography,
    Divider,
    Box,
    Collapse,
    ListItemText
} from '@mui/material'

import { onAuthStateChanged } from '@firebase/auth';
import { auth } from '../../../services/FirebaseConfig';

import { logout } from '../../../services/Authentication';
import { closeDrawer, openDrawer } from '../../../redux/reducers/DrawerSlice';
import LoaderCircular from '../../LoaderCircular';
import Main from './Main';
import AppBar from './AppBar';
import DrawerHeader from './DrawerHeader';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItemIcon from '@mui/material/ListItemIcon';
import HomeIcon from '@mui/icons-material/Home';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import ListItemButton from '@mui/material/ListItemButton';
import CategoryIcon from '@mui/icons-material/Category';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import AddIcon from '@mui/icons-material/Add';


let user;

onAuthStateChanged(auth, currentUser => {
    user = currentUser
})

const DrawerLayout = ({ children, headerTitle, projectId = null }) => {
    const theme = useTheme();
    const navigate = useNavigate()
    const dispatch = useDispatch();

    const projects = useSelector(state => state.projects)
    const drawer = useSelector(state => state.drawer)

    const [open, setOpen] = useState(undefined);
    const [openNestedList, setOpenNestedList] = useState(false)

    const pathName = window.location.pathname;
    const userEmail = user.email;

    const screenIsUnderTabletWidth = window.innerWidth < 900;

    useEffect(() => {
        if (projectId) {
            setOpenNestedList(true)
        }
        if (screenIsUnderTabletWidth) {
            handleDrawerClose()
        }
    }, [projectId])

    useEffect(() => {
        setOpen(drawer)
    }, [drawer])

    const handleNavigateToOtherPage = (screenName) => {
        switch (screenName) {
            case 'home':
                navigate('/')
                break;
            case 'createProject':
                navigate('/createProject')
                break;
            case 'setting':
                navigate('/setting')
                break;
            default:
                alert('Erreur de navigation')
                break;
        }
    }

    const handleDrawerOpen = () => {
        dispatch(openDrawer({}))
    };

    const handleDrawerClose = () => {
        dispatch(closeDrawer({}))
    };

    const handleLogout = () => {
        logout()
            .then(() => {
                window.location.reload()
            })
            .catch(err => {
                console.log(err);
            })
    }

    const toggleNestedList = () => {
        setOpenNestedList(!openNestedList)
    }

    const handleNavigateToProject = (projectId) => {
        navigate(`/project/${projectId}`)
    }

    if (open === undefined) {
        return (
            <LoaderCircular open={true} />
        )
    }

    return (
        <>
            {screenIsUnderTabletWidth ?
                <Box
                    sx={{
                        position: 'fixed',
                        backgroundColor: '#000',
                        opacity: 0.5,
                        zIndex: 2,
                        width: "100%",
                        height: "100%",
                        top: 0,
                        left: 0,
                        display: open ? "flex" : "none",
                    }}
                    onClick={() => handleDrawerClose()}
                />
                :
                null
            }
            <Box sx={{ display: 'flex' }}>
                <AppBar position="fixed" open={open}>
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            edge="start"
                            sx={{ mr: 2, ...(open && { display: 'none' }) }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                            <Typography variant="h6" noWrap sx={{ maxWidth: "50vw", fontSize: { xs: 16, md: 20 } }}>
                                {headerTitle}
                            </Typography>
                            <Typography variant="subtitle1" noWrap sx={{ fontSize: { xs: 12, md: 16 }, textAlign: 'end' }} >
                                {userEmail}
                            </Typography>
                        </Box>
                    </Toolbar>
                </AppBar>

                <Drawer
                    sx={{
                        width: 250,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: 250,
                            boxSizing: 'border-box',
                        },
                    }}
                    variant="persistent"
                    anchor="left"
                    open={open}
                >
                    <DrawerHeader>
                        <Typography fontSize={36} fontWeight='bold' sx={{ width: '100%', textAlign: 'left', marginLeft: 1 }} >Spliit</Typography>
                        <IconButton onClick={handleDrawerClose}>
                            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </DrawerHeader>

                    <Divider />

                    <List>
                        <ListItemButton onClick={() => handleNavigateToOtherPage('home')} selected={pathName === '/'}>
                            <ListItemIcon>
                                <HomeIcon />
                            </ListItemIcon>
                            <ListItemText primary={'Accueil'} />
                        </ListItemButton>

                        <ListItemButton onClick={() => toggleNestedList()} selected={false}>
                            <ListItemIcon>
                                <CategoryIcon />
                            </ListItemIcon>
                            <ListItemText primary={'Tous mes projets'} />

                            {openNestedList ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>

                        <Collapse in={openNestedList} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {projects.map((project, index) => {
                                    return (
                                        <ListItemButton
                                            onClick={() => handleNavigateToProject(project.projectId)}
                                            selected={projectId === project.projectId}
                                            key={index}
                                            sx={{ paddingLeft: 4 }}
                                        >
                                            <ListItemIcon>
                                                <ArrowRightIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={project.title} />
                                        </ListItemButton>
                                    )
                                })}
                            </List>
                        </Collapse>

                        <ListItemButton onClick={() => handleNavigateToOtherPage('createProject')} selected={pathName === '/createProject'}>
                            <ListItemIcon>
                                <AddIcon />
                            </ListItemIcon>
                            <ListItemText primary={'Nouveau projet'} />
                        </ListItemButton>
                    </List>

                    <Divider />

                    <List>
                        <ListItemButton selected={pathName === '/setting'} onClick={() => handleNavigateToOtherPage('setting')}>
                            <ListItemIcon>
                                <SettingsIcon />
                            </ListItemIcon>
                            <ListItemText primary={'Paramètres'} />
                        </ListItemButton>

                        <ListItemButton selected={false} onClick={() => handleLogout()}>
                            <ListItemIcon >
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText primary={'Déconnexion'} />
                        </ListItemButton>
                    </List>
                </Drawer>

                <Main open={open}>
                    <DrawerHeader />
                    {children}
                </Main>

            </Box>
        </>
    );
}

export default DrawerLayout;